import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Response } from 'cross-fetch'
import debounce from 'lodash.debounce'

import { useAppDispatch, useAppSelector } from 'state/store'

import {
  AutocompleteSuggestion,
  FilteractionDataSource,
  FlatInteractionModelData
} from 'types/shared'
import api from 'utils/api'
import { FilteractionParams } from 'utils/apiTypes'
import { FetchSuggestionsHook } from './useSuggestionsFromSources'

type FetchSuggestionsArgsProps = Parameters<FetchSuggestionsHook>[0] & {
  activityValues?: FlatInteractionModelData['activity_values']
}

const useFetchSuggestions: FetchSuggestionsHook = ({
  parentPath,
  filteractionParams: legacyFilteractionParams,
  filteraction: legacyFilteraction,
  endpoint: legacyEndpoint,
  showLoading = false,
  projectFields,
  dataSources
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([])

  const filteractionDataSource = dataSources?.find(
    (dataSource): dataSource is FilteractionDataSource =>
      dataSource.type === 'filteraction'
  )

  const {
    filteraction: filteractionFromSource,
    interpolated_params: filteractionParamsFromSource,
    endpoint: endpointFromSource
  } = filteractionDataSource?.config || {}

  const filteraction = filteractionFromSource || legacyFilteraction
  const filteractionParams =
    filteractionParamsFromSource || legacyFilteractionParams
  const endpoint = endpointFromSource || legacyEndpoint

  const ignoreResponse = useRef(false)

  const fetchSuggestionsSyncedArgs = useRef<FetchSuggestionsArgsProps>({
    parentPath,
    filteractionParams,
    filteraction,
    endpoint,
    showLoading,
    activityValues: {}
  })

  const dispatch = useAppDispatch()

  const resetSuggestions = () => {
    debouncedFetchSuggestions.cancel()
    ignoreResponse.current = true

    setIsLoading(false)
    setSuggestions([])
  }

  const interactionModel = useAppSelector((state) => state.interactionModel)

  useEffect(() => {
    const activityValues = interactionModel?.flatData?.activity_values

    fetchSuggestionsSyncedArgs.current = {
      parentPath,
      filteractionParams,
      filteraction,
      endpoint,
      showLoading,
      activityValues: activityValues
    }
  }, [
    endpoint,
    showLoading,
    filteraction,
    filteractionParams,
    interactionModel?.flatData?.activity_values,
    parentPath
  ])

  const fetchSuggestions = useCallback(
    async (value: string) => {
      ignoreResponse.current = false

      const {
        parentPath,
        filteractionParams,
        filteraction,
        endpoint,
        showLoading,
        activityValues
      } = fetchSuggestionsSyncedArgs.current

      try {
        setIsLoading(true)

        const throwError = false

        const response = await api.getAutocomplete(
          value,
          endpoint || '',
          !!showLoading,
          filteraction,
          filteractionParams as FilteractionParams[],
          activityValues || {},
          parentPath,
          throwError,
          projectFields
        )

        if (response.objects) {
          setSuggestions(
            !Array.isArray(response.objects) ? [] : response.objects
          )
        } else {
          if (!ignoreResponse.current) {
            throw new Error(response?.message || 'Response vazia ou com erro')
          }
        }
      } catch (error) {
        if (!ignoreResponse.current) {
          if (error instanceof Response) {
            dispatch({ type: 'api/ERROR', data: { status: error.status } })
          }

          console.error(error)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, projectFields]
  )

  const debouncedFetchSuggestions = useMemo(
    () => debounce(fetchSuggestions, 350),
    [fetchSuggestions]
  )

  return {
    suggestions,
    setSuggestions,
    isLoading,
    fetchSuggestions,
    debouncedFetchSuggestions,
    resetSuggestions
  }
}

export default useFetchSuggestions
